import { useEffect, useMemo, useRef, useState } from "react";
import SelectAllList from "../../../../../components/common/SelectAllList/SelectAllList";
import StatusBadge from "../../../../../components/common/StatusBadge/StatusBadge";
import TruncateTooltip from "../../../../../components/common/TruncateTooltip/TruncateTooltip";
import type {
  ChannelCatalogItem,
  ConnectionHubCloseReason,
  InboxGroup,
  SetupResponse,
  StandaloneCard,
  SubBrand,
} from "../../../api/connectionHub.types";
import { CONNECTION_HUB_CLOSE_REASON } from "../../../api/connectionHub.types";
import { connectionHubApi } from "../../../api/connectionHub.api";
import { isConnectionHubError } from "../../../utils/handleConnectionError";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import ConnectionLogo from "../../common/ConnectionLogo/ConnectionLogo";
import ConnectModalShell from "../ConnectModalShell/ConnectModalShell";
import styles from "./SubBrandStep.module.scss";

export interface SubBrandStepProps {
  visible: boolean;
  setup: SetupResponse;
  channels: ChannelCatalogItem[];
  agencyClientId: number | null;
  onComplete: (card: InboxGroup | StandaloneCard) => void;
  onBack?: () => void;
  onClose: (reason: ConnectionHubCloseReason) => void;
}

interface PendingTarget {
  connectionId: string;
  ipId: string;
  ipName: string;
  email: string | null;
  pending: SubBrand[];
  selected: SubBrand[];
}

const findPendingTarget = (setup: SetupResponse): PendingTarget | null => {
  for (const group of setup.inboxes) {
    const cover = group.covers.find((item) => item.pendingSubBrands.length > 0);
    if (cover) return { connectionId: cover.connectionId, ipId: cover.ipId, ipName: cover.ipName, email: group.inbox.email, pending: cover.pendingSubBrands, selected: cover.selectedSubBrands };
  }
  const card = setup.standalone.find((item) => item.connection.pendingSubBrands.length > 0);
  return card ? {
    connectionId: card.connection.connectionId,
    ipId: card.connection.ipId,
    ipName: card.connection.ipName,
    email: card.connection.loginEmail,
    pending: card.connection.pendingSubBrands,
    selected: card.connection.selectedSubBrands,
  } : null;
};

const propertyKey = (brandId: string, propertyId: string): string => `${brandId}::${propertyId}`;

const selectionIdsOf = (brands: SubBrand[], available: SubBrand[]): string[] => {
  const pendingById = new Map(available.map((brand) => [String(brand.id), brand]));
  return brands.flatMap((brand) => {
    const pending = pendingById.get(String(brand.id));
    if (!pending) return [];
    if (!pending.properties?.length) return [String(brand.id)];
    const known = new Set(pending.properties.map((property) => String(property.id)));
    return (brand.properties ?? [])
      .filter((property) => known.has(String(property.id)))
      .map((property) => propertyKey(String(brand.id), String(property.id)));
  });
};

const SubBrandStep = ({ visible, setup, channels, agencyClientId, onComplete, onBack, onClose }: SubBrandStepProps): JSX.Element => {
  const target = useMemo(() => findPendingTarget(setup), [setup]);
  const channel = channels.find((item) => item.ipId === target?.ipId);
  const hydratedTarget = useRef<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) {
      hydratedTarget.current = null;
      return setSelectedIds([]);
    }
    const targetKey = JSON.stringify({
      connectionId: target.connectionId,
      pending: target.pending.map((brand) => String(brand.id)),
      selected: target.selected.map((brand) => String(brand.id)),
    });
    if (hydratedTarget.current === targetKey) return;
    hydratedTarget.current = targetKey;
    setSelectedIds(selectionIdsOf(target.selected, target.pending));
  }, [target]);

  const save = async (): Promise<void> => {
    if (!target || !selectedIds.length || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const selected = new Set(selectedIds);
      const subBrands = target.pending.flatMap((brand) => {
        const id = String(brand.id);
        if (!brand.properties?.length) return selected.has(id) ? [{ id: brand.id, name: brand.name }] : [];
        const properties = brand.properties
          .filter((property) => selected.has(propertyKey(id, String(property.id))))
          .map(({ id: propertyId, name }) => ({ id: propertyId, name }));
        return properties.length ? [{ id: brand.id, name: brand.name, properties }] : [];
      });
      const card = await connectionHubApi.saveSubBrands(target.connectionId, { agencyClientId, subBrands });
      onComplete(card);
    } catch (caught) {
      if (isConnectionHubError(caught)) setError(caught.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConnectModalShell
      visible={visible}
      title="Select Sub-Brands"
      bodyClassName={styles.modalBody}
      primaryLabel="Done"
      primaryLoading={submitting}
      primaryDisabled={!target || !selectedIds.length || submitting}
      onPrimary={() => { void save(); }}
      {...(onBack ? { backLabel: "Back", onBack } : {})}
      onClose={() => onClose(CONNECTION_HUB_CLOSE_REASON.CLOSE_BUTTON)}
      onDismiss={(reason) => onClose(reason === "escape" ? CONNECTION_HUB_CLOSE_REASON.ESCAPE : CONNECTION_HUB_CLOSE_REASON.BACKDROP)}
    >
      {target ? (
        <article className={styles.card}>
          <header className={styles.header}>
            <div className={styles.identity}>
              <ConnectionLogo logoUrl={channel?.logo} className={styles.logo} />
              <div className={styles.identityText}>
                {target.email ? <TruncateTooltip text={target.email} className={styles.email} /> : null}
                <TruncateTooltip text={target.ipName} className={styles.marketplace} />
              </div>
            </div>
            {selectedIds.length
              ? <StatusBadge label="Connected" variant="green" />
              : <StatusBadge label="Sub-Brands Pending" variant="amber" />}
          </header>
          <h3>Select your <span className={styles.highlight}>{target.ipName} - Sub Brands</span></h3>
          <SelectAllList
            items={target.pending.map((brand) => ({
              id: String(brand.id),
              label: brand.name,
              children: brand.properties?.map((property) => ({
                id: propertyKey(String(brand.id), String(property.id)),
                label: property.name,
              })),
            }))}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            disabled={submitting}
          />
          <ConnectionErrorPanel error={error} />
        </article>
      ) : <p className={styles.empty}>Sub-brand choices are no longer pending.</p>}
    </ConnectModalShell>
  );
};

export default SubBrandStep;
